import React, { useState, useEffect, useRef } from 'react';
import { Container, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import {
  BookOpenIcon,
  ChevronRightIcon,
  BuildingStorefrontIcon,
  ShoppingBagIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/solid';
import PublicNavbar from './common/PublicNavbar';
import PublicFooter from './common/PublicFooter';
import { siteConfigAPI } from '../services/api';

/* ─── Types matching API response ─── */
interface GuideSection {
  id: number;
  title: string;
  slug: string;
  group: string;
  group_label: string;
  content: string;
  sort_order: number;
}

/* ─── Group metadata ─── */
const GROUP_META: Record<string, { label: string; color: string; icon: React.ElementType }> = {
  owner: { label: 'For Bakery Owners', color: '#3b82f6', icon: BuildingStorefrontIcon },
  customer: { label: 'For Wholesale Customers', color: '#10b981', icon: ShoppingBagIcon },
  security: { label: 'Account & Security', color: '#ef4444', icon: ShieldCheckIcon },
};

const GROUP_ORDER = ['owner', 'customer', 'security'];

/* ─── Main component ─── */
const UserGuide: React.FC = () => {
  const [sections, setSections] = useState<GuideSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState('');
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.title = 'User Guide | Sourdoughsy';
    siteConfigAPI.getGuideSectionsPublic()
      .then(res => setSections(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  /* Track section in viewport */
  useEffect(() => {
    if (sections.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter(e => e.isIntersecting);
        if (visible.length > 0) setActiveId(visible[0].target.id);
      },
      { rootMargin: '-100px 0px -60% 0px', threshold: 0 }
    );
    const els = contentRef.current?.querySelectorAll('section[id]');
    els?.forEach(s => observer.observe(s));
    return () => observer.disconnect();
  }, [sections]);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  /* Group sections for TOC and rendering */
  const grouped = GROUP_ORDER
    .map(g => ({ group: g, ...GROUP_META[g], items: sections.filter(s => s.group === g) }))
    .filter(g => g.items.length > 0);

  /* Global numbering across all sections */
  let sectionNum = 0;
  const numberMap = new Map<number, number>();
  grouped.forEach(g => g.items.forEach(s => { sectionNum++; numberMap.set(s.id, sectionNum); }));

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <PublicNavbar solid />

      {/* Hero */}
      <div style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 100%)',
        paddingTop: '120px', paddingBottom: '48px', textAlign: 'center',
      }}>
        <Container>
          <div className="d-flex align-items-center justify-content-center gap-2 mb-2">
            <BookOpenIcon style={{ width: 28, height: 28, color: 'rgba(255,255,255,0.6)' }} />
            <h1 style={{ color: 'white', fontWeight: 800, fontSize: 'clamp(1.8rem, 4vw, 2.6rem)', letterSpacing: '-0.5px', marginBottom: 0 }}>
              User Guide
            </h1>
          </div>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '1rem', marginBottom: 0 }}>
            Everything you need to know about using Sourdoughsy
          </p>
        </Container>
      </div>

      {/* Content */}
      <div style={{ flex: 1, background: '#f8fafc', padding: '48px 0 80px' }}>
        <Container style={{ maxWidth: '960px' }}>

          {/* Quick link to Help Center */}
          <div className="text-center mb-4" style={{ fontSize: '0.85rem', color: '#64748b' }}>
            Looking for quick answers? Try the{' '}
            <Link to="/help" style={{ color: '#3b82f6', fontWeight: 600 }}>Help Center</Link>
          </div>

          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="secondary" /></div>
          ) : sections.length === 0 ? (
            <div className="text-center py-5" style={{ color: '#94a3b8' }}>No guide content available yet.</div>
          ) : (
            <>
              {/* Table of contents card */}
              <div style={{
                background: 'white', borderRadius: '16px', padding: 'clamp(20px, 4vw, 32px)',
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)', marginBottom: '32px',
              }}>
                <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1f2937', marginBottom: '20px' }}>
                  Table of Contents
                </h2>
                {grouped.map(g => {
                  const Icon = g.icon;
                  return (
                    <div key={g.group} style={{ marginBottom: '16px' }}>
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <Icon style={{ width: 18, height: 18, color: g.color }} />
                        <span style={{ fontWeight: 700, fontSize: '0.9rem', color: g.color }}>{g.label}</span>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '4px 16px', paddingLeft: '26px' }}>
                        {g.items.map(item => {
                          const num = numberMap.get(item.id)!;
                          return (
                            <button
                              key={item.slug}
                              onClick={() => scrollTo(item.slug)}
                              style={{
                                background: 'none', border: 'none', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', gap: '6px',
                                padding: '4px 0', textAlign: 'left',
                                color: activeId === item.slug ? '#3b82f6' : '#475569',
                                fontWeight: activeId === item.slug ? 600 : 400,
                                fontSize: '0.85rem', transition: 'color 0.15s',
                              }}
                            >
                              <ChevronRightIcon style={{ width: 12, height: 12, flexShrink: 0 }} />
                              {num}. {item.title}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Guide content */}
              <div ref={contentRef} style={{
                background: 'white', borderRadius: '16px', padding: 'clamp(20px, 4vw, 32px)',
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              }}>
                {grouped.map((g, gi) => {
                  const Icon = g.icon;
                  return (
                    <React.Fragment key={g.group}>
                      <div className={`d-flex align-items-center gap-2 ${gi > 0 ? 'mt-5' : ''} mb-4`}
                        style={{ borderBottom: `2px solid ${g.color}`, paddingBottom: '12px' }}>
                        <Icon style={{ width: 22, height: 22, color: g.color }} />
                        <h2 style={{ fontSize: '1.2rem', fontWeight: 800, color: g.color, marginBottom: 0 }}>{g.label}</h2>
                      </div>
                      {g.items.map(s => {
                        const num = numberMap.get(s.id)!;
                        return (
                          <section key={s.id} id={s.slug} style={{ scrollMarginTop: '100px', marginBottom: '40px' }}>
                            <div className="d-flex align-items-center gap-2 mb-3">
                              <span style={{
                                background: '#3b82f6', color: 'white', width: 28, height: 28, borderRadius: '8px',
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                fontWeight: 700, fontSize: '0.8rem', flexShrink: 0,
                              }}>{num}</span>
                              <h2 style={{ fontSize: '1.3rem', fontWeight: 700, color: '#1f2937', marginBottom: 0 }}>{s.title}</h2>
                            </div>
                            <div
                              className="guide-content"
                              style={{ color: '#334155', fontSize: '0.92rem', lineHeight: 1.8 }}
                              dangerouslySetInnerHTML={{ __html: s.content }}
                            />
                          </section>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </div>
            </>
          )}

          {/* Bottom CTA */}
          <div style={{
            background: 'white', borderRadius: '16px', padding: '32px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)', textAlign: 'center', marginTop: '32px',
          }}>
            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1f2937', marginBottom: '8px' }}>
              Need more help?
            </h3>
            <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '16px' }}>
              Check our Help Center for quick answers, or reach out to our team.
            </p>
            <div className="d-flex justify-content-center gap-3 flex-wrap">
              <Link
                to="/help"
                style={{
                  display: 'inline-block', background: 'white', color: '#3b82f6', padding: '10px 24px',
                  borderRadius: '8px', fontWeight: 600, fontSize: '0.9rem', textDecoration: 'none',
                  border: '2px solid #3b82f6',
                }}
              >
                Help Center
              </Link>
              <Link
                to="/contact"
                style={{
                  display: 'inline-block', background: '#3b82f6', color: 'white', padding: '10px 24px',
                  borderRadius: '8px', fontWeight: 600, fontSize: '0.9rem', textDecoration: 'none',
                }}
              >
                Contact Us
              </Link>
            </div>
          </div>

        </Container>
      </div>

      <PublicFooter />
    </div>
  );
};

export default UserGuide;
